import React, { useState, useEffect } from 'react';
import {
  X, Video, Home as HomeIcon, Pill, Calendar, Clock,
  ChevronRight, Loader2, CheckCircle, Copy, ExternalLink, CalendarClock
} from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const ACTION_CONFIG = {
  virtual_visit: {
    label: 'Schedule Virtual Visit',
    icon: Video,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    description: 'A video call link will be generated and sent to the patient.',
    appointmentType: 'virtual_visit',
  },
  home_visit: {
    label: 'Order Home Visit',
    icon: HomeIcon,
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    description: 'A nurse or home health aide will visit the patient.',
    appointmentType: 'home_visit',
  },
  pharmacist: {
    label: 'Pharmacist Consult',
    icon: Pill,
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    description: 'A pharmacist will review the patient\'s medications.',
    appointmentType: 'pharmacist_consult',
  },
  follow_up_scheduling: {
    label: 'Schedule Follow-up',
    icon: CalendarClock,
    color: 'bg-teal-50 text-teal-700 border-teal-200',
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    description: 'Schedule the required follow-up visit for this patient.',
    appointmentType: null, // resolved from followUpType prop or user selection
  },
};

// Build the next 14 days as date options
const buildDateOptions = () => {
  const opts = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    d.setHours(0, 0, 0, 0);
    opts.push(d);
  }
  return opts;
};

const DATE_OPTIONS = buildDateOptions();

const TIME_SLOTS = [];
for (let h = 7; h <= 18; h++) {
  for (const m of [0, 30]) {
    if (h === 18 && m === 30) continue;
    const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    const label = `${hour12}:${m === 0 ? '00' : '30'} ${ampm}`;
    TIME_SLOTS.push({ label, hour: h, minute: m });
  }
};

const ClinicianActionModal = ({ actionKey, patientId, symptomId, followUpType, followUpDate, onClose, onScheduled, onActionExecuted }) => {
  const config = ACTION_CONFIG[actionKey];

  // For follow_up_scheduling, doctor can choose the visit type
  const [selectedFollowUpType, setSelectedFollowUpType] = useState(followUpType || 'virtual_visit');

  // Pre-select date if followUpDate was provided; otherwise default to tomorrow
  const initialDate = (() => {
    if (followUpDate) {
      const d = new Date(followUpDate);
      const match = DATE_OPTIONS.find(o => o.toDateString() === d.toDateString());
      return match || DATE_OPTIONS[1] || DATE_OPTIONS[0];
    }
    return DATE_OPTIONS[1] || DATE_OPTIONS[0];
  })();

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedTime, setSelectedTime] = useState(TIME_SLOTS[4]); // default 9:00 AM
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { meetingLink, scheduledDate, appointmentType }

  // Resolve the actual appointment type
  const resolvedAppointmentType = actionKey === 'follow_up_scheduling'
    ? selectedFollowUpType
    : config?.appointmentType;

  useEffect(() => {
    // Prevent body scroll while modal is open
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!config) return null;

  const Icon = config.icon;

  const buildScheduledDate = () => {
    const d = new Date(selectedDate);
    d.setHours(selectedTime.hour, selectedTime.minute, 0, 0);
    return d.toISOString();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await api.post('/care-events/schedule-appointment', {
        patientId,
        appointmentType: resolvedAppointmentType,
        scheduledDate: buildScheduledDate(),
        symptomId: symptomId || undefined,
        notes: notes.trim() || undefined,
      });
      if (response.data.success) {
        setResult(response.data.data);
        if (onScheduled) onScheduled(response.data.data);
        if (onActionExecuted) onActionExecuted();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to schedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(result.meetingLink);
    toast.success('Meeting link copied');
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (result) {
    const d = new Date(result.scheduledDate);
    const dateStr = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return (
      <ModalShell onClose={onClose}>
        <div className="p-6 text-center">
          <div className="w-14 h-14 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-success-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-1">{config.label} Scheduled</h3>
          <p className="text-sm text-slate-500 mb-5">{dateStr} at {timeStr}</p>

          {result.meetingLink && (
            <div className="bg-blue-50 rounded-xl p-4 mb-5 text-left">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">Video Call Link</p>
              <p className="text-xs text-blue-600 break-all font-mono mb-3">{result.meetingLink}</p>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 rounded-lg text-xs font-bold text-blue-700 hover:bg-blue-50 transition-all"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
                <a
                  href={result.meetingLink}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 rounded-lg text-xs font-bold text-white hover:bg-blue-700 transition-all"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open Jitsi
                </a>
              </div>
            </div>
          )}

          <p className="text-sm text-slate-500 mb-5">
            The patient has been notified and will receive the details.
          </p>

          <button
            onClick={onClose}
            className="w-full py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-all"
          >
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <ModalShell onClose={onClose}>
      {/* Header */}
      <div className="p-5 border-b border-slate-100 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.iconBg}`}>
          <Icon className={`w-5 h-5 ${config.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900">{config.label}</h3>
          <p className="text-xs text-slate-500">{config.description}</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Visit type selector — follow_up_scheduling only */}
        {actionKey === 'follow_up_scheduling' && (
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
              <CalendarClock className="w-4 h-4 text-slate-400" /> Visit type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'virtual_visit', label: 'Virtual', Icon: Video },
                { value: 'home_visit', label: 'Home Visit', Icon: HomeIcon },
                { value: 'pharmacist_consult', label: 'Pharmacist', Icon: Pill },
              ].map(({ value, label, Icon }) => (
                <button
                  key={value}
                  onClick={() => setSelectedFollowUpType(value)}
                  className={`flex flex-col items-center gap-1 py-3 rounded-xl text-xs font-bold transition-all border
                    ${selectedFollowUpType === value
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-primary/40 hover:bg-primary/5'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Date picker */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
            <Calendar className="w-4 h-4 text-slate-400" /> Date
          </label>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-7">
            {DATE_OPTIONS.map((date, i) => {
              const isSelected = date.toDateString() === selectedDate.toDateString();
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const dayNum = date.getDate();
              const isToday = i === 0;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDate(date)}
                  className={`flex flex-col items-center py-2.5 px-1 rounded-xl text-xs font-bold transition-all border
                    ${isSelected
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-primary/40 hover:bg-primary/5'
                    }`}
                >
                  <span className={`uppercase tracking-wider text-[10px] ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>{dayName}</span>
                  <span className="text-base mt-0.5">{dayNum}</span>
                  {isToday && <span className={`text-[9px] font-bold mt-0.5 ${isSelected ? 'text-white/70' : 'text-primary'}`}>Today</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time picker */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
            <Clock className="w-4 h-4 text-slate-400" /> Time
          </label>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
            {TIME_SLOTS.map((slot, i) => {
              const isSelected = slot.hour === selectedTime.hour && slot.minute === selectedTime.minute;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedTime(slot)}
                  className={`py-2 px-1 rounded-xl text-xs font-bold transition-all border
                    ${isSelected
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-primary/40 hover:bg-primary/5'
                    }`}
                >
                  {slot.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm font-semibold text-slate-700 mb-1 block">
            Notes <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              actionKey === 'virtual_visit' ? 'e.g. We will discuss your recent symptoms and test results...'
              : actionKey === 'home_visit' ? 'e.g. Nurse will check blood pressure and wound dressing...'
              : 'e.g. Please bring a list of all current medications...'
            }
            className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none bg-slate-50 focus:bg-white transition-all min-h-[72px]"
          />
        </div>

        {/* Summary preview */}
        <div className={`rounded-xl p-3 border ${config.color} text-sm`}>
          <p className="font-semibold">
            {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {selectedTime.label}
          </p>
          {actionKey === 'virtual_visit' && (
            <p className="text-xs mt-1 opacity-80">A Jitsi video call link will be generated and sent to the patient.</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 py-3 border-2 border-slate-200 text-slate-700 rounded-full font-semibold hover:bg-slate-50 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 py-3 bg-gradient-to-r from-primary to-primary-container text-white rounded-full font-semibold disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          >
            {isSubmitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Scheduling...</>
            ) : (
              <>Confirm <ChevronRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

const ModalShell = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      {children}
    </div>
  </div>
);

export default ClinicianActionModal;
